/**
 * Editorial Product Feed — CRTZ.xyz Inspired
 * Vanilla JS: Infinite scroll, lazy loading, scroll animations, sidebar interaction
 */

(function () {
  'use strict';

  /* ============================================================
     STATE
     ============================================================ */
  const STATE = {
    currentPage: 1,
    totalPages: 1,
    isLoading: false,
    hasMore: true,
    currentCategory: null,
    collectionHandle: '',
    sectionId: '',
    productsPerPage: 12,
  };

  /* ============================================================
     DOM REFERENCES
     ============================================================ */
  const DOM = {};

  function cacheDom() {
    DOM.section = document.querySelector('.editorial-feed');
    if (!DOM.section) return false;

    DOM.grid = DOM.section.querySelector('.editorial-feed__grid');
    DOM.sentinel = DOM.section.querySelector('.editorial-feed__sentinel');
    DOM.loader = DOM.section.querySelector('.editorial-feed__loader');
    DOM.endMessage = DOM.section.querySelector('.editorial-feed__end');
    DOM.progressBar = DOM.section.querySelector('.editorial-feed__progress-bar');
    
    // Global sidebar elements
    DOM.sidebar = document.querySelector('.global-sidebar');
    DOM.navLinks = document.querySelectorAll('.editorial-feed__nav-link');
    
    DOM.categoryTitle = DOM.section.querySelector('.editorial-feed__category-title');

    STATE.collectionHandle = DOM.section.dataset.collectionHandle || '';
    STATE.sectionId = DOM.section.dataset.sectionId || '';
    STATE.totalPages = parseInt(DOM.section.dataset.totalPages, 10) || 1;
    STATE.productsPerPage = parseInt(DOM.section.dataset.productsPerPage, 10) || 12;

    return true;
  }

  /* ============================================================
     LAZY LOADING IMAGES
     ============================================================ */
  function initLazyLoading() {
    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.dataset.src;
            const srcset = img.dataset.srcset;

            if (src) {
              img.src = src;
              img.removeAttribute('data-src');
            }
            if (srcset) {
              img.srcset = srcset;
              img.removeAttribute('data-srcset');
            }

            img.addEventListener('load', () => {
              img.classList.add('editorial-feed__card-image--loaded');
            }, { once: true });

            // Fallback: if image already cached
            if (img.complete && img.naturalWidth > 0) {
              img.classList.add('editorial-feed__card-image--loaded');
            }

            imageObserver.unobserve(img);
          }
        });
      },
      {
        rootMargin: '200px 0px',
        threshold: 0.01,
      }
    );

    DOM.section.querySelectorAll('.editorial-feed__card-image--lazy').forEach((img) => {
      imageObserver.observe(img);
    });

    return imageObserver;
  }

  /* ============================================================
     SCROLL REVEAL ANIMATIONS
     ============================================================ */
  function initScrollReveal() {
    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('editorial-feed__card--visible');
            cardObserver.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '0px 0px -60px 0px',
        threshold: 0.1,
      }
    );

    DOM.section.querySelectorAll('.editorial-feed__card').forEach((card) => {
      cardObserver.observe(card);
    });

    return cardObserver;
  }

  /* ============================================================
     INFINITE SCROLL
     ============================================================ */
  function initInfiniteScroll() {
    if (!DOM.sentinel) return;

    const scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !STATE.isLoading && STATE.hasMore) {
            loadMoreProducts();
          }
        });
      },
      {
        rootMargin: '400px 0px',
        threshold: 0.01,
      }
    );

    scrollObserver.observe(DOM.sentinel);
  }

  async function loadMoreProducts() {
    if (STATE.isLoading || !STATE.hasMore) return;
    if (STATE.currentPage >= STATE.totalPages) {
      STATE.hasMore = false;
      showEndMessage();
      return;
    }

    STATE.isLoading = true;
    showLoader();

    const nextPage = STATE.currentPage + 1;

    try {
      const url = buildPageUrl(nextPage);
      const response = await fetch(url);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newCards = doc.querySelectorAll('.editorial-feed__card');

      if (newCards.length === 0) {
        STATE.hasMore = false;
        showEndMessage();
      } else {
        appendCards(newCards);
        STATE.currentPage = nextPage;

        if (nextPage >= STATE.totalPages) {
          STATE.hasMore = false;
          showEndMessage();
        }
      }
    } catch (err) {
      console.error('[Editorial Feed] Failed to load page:', err);
      // Retry after delay
      setTimeout(() => {
        STATE.isLoading = false;
      }, 3000);
      return;
    }

    STATE.isLoading = false;
    hideLoader();
  }

  function buildPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);

    // Use Shopify section rendering API for cleaner fetching
    if (STATE.sectionId) {
      url.searchParams.set('section_id', STATE.sectionId);
    }

    return url.toString();
  }

  function appendCards(cards) {
    const fragment = document.createDocumentFragment();

    cards.forEach((card, index) => {
      const clone = card.cloneNode(true);
      clone.classList.remove('editorial-feed__card--visible');

      // Stagger entrance
      clone.style.transitionDelay = `${index * 0.08}s`;

      fragment.appendChild(clone);
    });

    DOM.grid.appendChild(fragment);

    // Re-initialize observers for new cards
    requestAnimationFrame(() => {
      initLazyLoading();
      initScrollReveal();
    });
  }

  function showLoader() {
    if (DOM.loader) {
      DOM.loader.classList.add('editorial-feed__loader--active');
    }
  }

  function hideLoader() {
    if (DOM.loader) {
      DOM.loader.classList.remove('editorial-feed__loader--active');
    }
  }

  function showEndMessage() {
    hideLoader();
    if (DOM.endMessage) {
      DOM.endMessage.classList.add('editorial-feed__end--visible');
    }
  }

  /* ============================================================
     SIDEBAR NAVIGATION
     ============================================================ */
  function initSidebarNav() {
    DOM.navLinks.forEach((link) => {
      link.addEventListener('click', handleNavClick);
    });

    // Set first nav item as active by default
    if (DOM.navLinks.length > 0 && !DOM.section.querySelector('.editorial-feed__nav-link--active')) {
      DOM.navLinks[0].classList.add('editorial-feed__nav-link--active');
    }
  }

  function handleNavClick(e) {
    const link = e.currentTarget;
    const collectionHandle = link.dataset.collection;
    const categoryName = link.dataset.category;

    // Only intercept collection links. Let standard links navigate normally.
    if (!collectionHandle) {
      return;
    }

    e.preventDefault();

    // Update active state
    DOM.navLinks.forEach((l) => l.classList.remove('editorial-feed__nav-link--active'));
    link.classList.add('editorial-feed__nav-link--active');

    // Scroll active link into view on mobile (if we are using the sidebar, which is hidden now on mobile)
    if (window.innerWidth <= 768 && DOM.sidebar) {
      link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    // Close the theme's native mobile drawer if it is open
    const openDrawerSummary = document.querySelector('header-drawer details[open] summary');
    if (openDrawerSummary) {
      openDrawerSummary.click();
    }

    // Update category title
    if (DOM.categoryTitle && categoryName) {
      DOM.categoryTitle.style.opacity = '0';
      setTimeout(() => {
        DOM.categoryTitle.textContent = categoryName;
        DOM.categoryTitle.style.opacity = '1';
      }, 300);
    }

    // Load new collection
    if (collectionHandle && collectionHandle !== STATE.collectionHandle) {
      loadCollection(collectionHandle);
    }
  }

  async function loadCollection(handle) {
    STATE.isLoading = true;
    STATE.currentPage = 1;
    STATE.hasMore = true;
    showLoader();

    // Fade out current grid
    DOM.grid.style.opacity = '0';
    DOM.grid.style.transform = 'translateY(20px)';

    try {
      const url = `/collections/${handle}?section_id=${STATE.sectionId}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('.editorial-feed__grid');
      const newSection = doc.querySelector('.editorial-feed');

      if (newGrid) {
        DOM.grid.innerHTML = newGrid.innerHTML;
        STATE.collectionHandle = handle;

        // Update total pages from new section data
        if (newSection) {
          STATE.totalPages = parseInt(newSection.dataset.totalPages, 10) || 1;
        }

        // Re-init observers
        requestAnimationFrame(() => {
          initLazyLoading();
          initScrollReveal();

          // Fade in new grid
          DOM.grid.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
          DOM.grid.style.opacity = '1';
          DOM.grid.style.transform = 'translateY(0)';
        });
      }

      // Hide end message
      if (DOM.endMessage) {
        DOM.endMessage.classList.remove('editorial-feed__end--visible');
      }
    } catch (err) {
      console.error('[Editorial Feed] Failed to load collection:', err);
    }

    STATE.isLoading = false;
    hideLoader();
  }

  /* ============================================================
     SCROLL PROGRESS BAR
     ============================================================ */
  function initProgressBar() {
    if (!DOM.progressBar) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateProgressBar();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  function updateProgressBar() {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

    DOM.progressBar.style.width = `${Math.min(scrollPercent, 100)}%`;
  }

  /* ============================================================
     INITIALIZATION
     ============================================================ */
  function init() {
    if (!cacheDom()) return;

    initLazyLoading();
    initScrollReveal();
    initInfiniteScroll();
    initSidebarNav();
    initProgressBar();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
